import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "@/lib/api-client";
import { useAuth } from "@/hooks/use-auth";
import { queryKeys } from "@/lib/query-keys";

/**
 * The caller's own email notification preferences.
 *
 * The route always acts on the signed-in user (the id comes from verified
 * claims on the server), so there is no user id in the key: one cache entry per
 * session, cleared like every other user-scoped query on sign-out.
 */

export interface EmailPreferences {
  groupActivity: boolean;
  reviewReminders: boolean;
  productUpdates: boolean;
  unsubscribedAll: boolean;
}

export interface EmailPreferencesResponse {
  email: string | null;
  preferences: EmailPreferences;
}

export type EmailPreferencePatch = Partial<EmailPreferences>;

export function useEmailPreferences(options: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const key = queryKeys.emailPreferences(userId);

  const query = useQuery({
    queryKey: key,
    queryFn: () => apiGet<EmailPreferencesResponse>("/v1/email/preferences"),
    // Keyed by user: there is no global cache reset on sign-out, and a shared
    // key would show one learner's settings (and address) to the next one.
    enabled: (options.enabled ?? true) && Boolean(userId),
    // Settings data changes only when this component changes it, so a stale
    // window is useless and a refetch on focus is just a round trip.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const mutation = useMutation({
    mutationFn: (patch: EmailPreferencePatch) =>
      apiPut<EmailPreferences>("/v1/email/preferences", patch),
    onSuccess: (preferences) => {
      queryClient.setQueryData<EmailPreferencesResponse>(key, (current) =>
        current ? { ...current, preferences } : current,
      );
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const update = useCallback(
    (patch: EmailPreferencePatch) => mutation.mutateAsync(patch),
    [mutation],
  );

  return {
    email: query.data?.email ?? null,
    preferences: query.data?.preferences ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    update,
    isUpdating: mutation.isPending,
    /** Which field is being saved — lets one row show its own pending state. */
    pendingField: (mutation.variables as EmailPreferencePatch | undefined) ?? null,
  };
}

export default useEmailPreferences;
